import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, deleteDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';
import SetupGuard from '../components/SetupGuard';
import { useSelectedLocation } from './LocationContext';

// Generate a random 16-byte salt as hex string
const generateSalt = () => {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
};

// Hash a PIN with SHA-256 using the provided salt
const hashPin = async (pin, salt) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(pin + salt);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

const ROLES = [
  { key: 'admin', label: 'Admin', color: '#dc3545' },
  { key: 'manager', label: 'Manager', color: '#ff6b35' },
  { key: 'cashier', label: 'Cashier', color: '#007bff' },
  { key: 'employee', label: 'Employee', color: '#6c757d' },
];

const EmployeesPage = ({ embedded = false }) => {
  const [user, setUser] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'employee',
    status: 'active',
    pin: '',
    locationId: '',
    payRate: '',
  });

  const navigate = useNavigate();
  const auth = getAuth();
  const { selectedLocation } = useSelectedLocation() || {};

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadEmployees(currentUser.uid);
        await loadLocations(currentUser.uid);
        setLoading(false);
      } else {
        navigate('/admin/login');
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, employees, selectedLocation]);

  const loadEmployees = async (uid) => {
    try {
      const db = getFirestore();
      const snapshot = await getDocs(
        query(collection(db, 'users', uid, 'employees'), orderBy('name'))
      );
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setEmployees(list);
    } catch (e) {
      console.error('Error loading employees:', e);
    }
  };

  const loadLocations = async (uid) => {
    try {
      const db = getFirestore();
      const snapshot = await getDocs(collection(db, 'users', uid, 'locations'));
      setLocations(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Error loading locations:', e);
    }
  };

  const filterEmployees = () => {
    let result = employees;
    // Filter by selected location from navbar
    if (selectedLocation?.id) {
      result = result.filter(e => e.locationId === selectedLocation.id);
    }
    // Apply search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(e =>
        (e.name || '').toLowerCase().includes(q) ||
        (e.email || '').toLowerCase().includes(q) ||
        (e.phone || '').toLowerCase().includes(q) ||
        (e.role || '').toLowerCase().includes(q)
      );
    }
    setFilteredEmployees(result);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      Swal.fire('Error', 'Name is required', 'error');
      return false;
    }
    if (!formData.locationId) {
      Swal.fire('Error', 'Location is required', 'error');
      return false;
    }
    if (!formData.role) {
      Swal.fire('Error', 'Role is required', 'error');
      return false;
    }
    // If a PIN is provided, validate format
    if (formData.pin && !/^\d{4,6}$/.test(formData.pin)) {
      Swal.fire('Error', 'Passkey must be 4 to 6 digits', 'error');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm() || !user) return;

    try {
      const db = getFirestore();
      const employeeData = {
        name: formData.name.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        role: formData.role,
        status: formData.status,
        locationId: formData.locationId || null,
        updatedAt: serverTimestamp(),
      };

      const payRateVal = formData.payRate.trim();
      if (payRateVal) {
        const parsed = parseFloat(payRateVal);
        if (!isNaN(parsed) && parsed >= 0) {
          employeeData.payRate = parsed;
        }
      } else if (editingEmployee) {
        // Explicitly clear if empty on edit (optional: remove to keep existing)
        employeeData.payRate = null;
      }

      // If a new PIN is provided, hash it with a salt
      if (formData.pin) {
        const salt = generateSalt();
        const pinHash = await hashPin(formData.pin, salt);
        employeeData.pinHash = pinHash;
        employeeData.pinSalt = salt;
        // Never store the raw PIN
      }

      if (editingEmployee) {
        // If PIN is empty on edit, keep existing hash
        if (!formData.pin) {
          delete employeeData.pinHash;
          delete employeeData.pinSalt;
        }
        await setDoc(doc(db, 'users', user.uid, 'employees', editingEmployee.id), employeeData, { merge: true });
        Swal.fire('Success', 'Employee updated!', 'success');
      } else {
        employeeData.createdAt = serverTimestamp();
        const docRef = doc(collection(db, 'users', user.uid, 'employees'));
        await setDoc(docRef, employeeData);
        Swal.fire('Success', 'Employee created!', 'success');
      }

      setShowModal(false);
      setEditingEmployee(null);
      setFormData({ name: '', email: '', phone: '', role: 'employee', status: 'active', pin: '', locationId: '', payRate: '' });
      await loadEmployees(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
    } catch (e) {
      console.error('Error saving employee:', e);
      Swal.fire('Error', e.message || 'Failed to save employee', 'error');
    }
  };

  const handleDelete = async (employee) => {
    const result = await Swal.fire({
      title: 'Delete Employee?',
      text: `Are you sure you want to delete "${employee.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed || !user) return;

    try {
      const db = getFirestore();
      await deleteDoc(doc(db, 'users', user.uid, 'employees', employee.id));
      Swal.fire('Deleted!', 'Employee has been deleted.', 'success');
      await loadEmployees(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
    } catch (e) {
      console.error('Error deleting employee:', e);
      Swal.fire('Error', e.message || 'Failed to delete employee', 'error');
    }
  };

  const openNew = () => {
    setEditingEmployee(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: 'employee',
      status: 'active',
      pin: '',
      locationId: selectedLocation?.id || (locations.length > 0 ? locations[0].id : ''),
      payRate: '',
    });
    setShowModal(true);
  };

  const openEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name || '',
      email: employee.email || '',
      phone: employee.phone || '',
      role: employee.role || 'employee',
      status: employee.status || 'active',
      pin: '', // Never pre-fill the raw PIN
      locationId: employee.locationId || '',
      payRate: employee.payRate != null ? String(employee.payRate) : '',
    });
    setShowModal(true);
  };

  const getLocationName = (id) => {
    const loc = locations.find(l => l.id === id);
    return loc ? loc.companyName || loc.locationName : '—';
  };

  const getRoleBadge = (role) => {
    const r = ROLES.find(x => x.key === role);
    return (
      <span
        className="badge"
        style={{
          backgroundColor: (r?.color || '#6c757d') + '20',
          color: r?.color || '#6c757d',
          fontSize: '0.8rem',
          borderRadius: 8,
        }}
      >
        {r?.label || role}
      </span>
    );
  };

  const content = (
    <SetupGuard prerequisites={['location']}>
      <>
      <div className="employees-page">
        {/* Header */}
        <div className="row mb-4 align-items-center">
          <div className="col-md-6">
            <h4 className="font-weight-bold">Employees</h4>
            <p className="text-muted">
              {selectedLocation?.id ? (
                <>
                  <span className="badge px-2 py-1" style={{ background: '#ff6b3520', color: '#ff6b35', borderRadius: 8 }}>
                    <i className="fas fa-store mr-1"></i>
                    {selectedLocation.companyName || selectedLocation.locationName}
                  </span>
                  <span className="ml-2">Showing staff for this location</span>
                </>
              ) : (
                'Manage staff, roles, and POS passkeys'
              )}
            </p>
          </div>
          <div className="col-md-6 text-md-right">
            <button className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }} onClick={openNew}>
              <i className="fas fa-plus mr-2"></i>Add Employee
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text bg-white border-right-0">
                  <i className="fas fa-search text-muted"></i>
                </span>
              </div>
              <input
                type="text"
                className="form-control border-left-0"
                placeholder="Search by name, email, phone, or role..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ borderRadius: '0 8px 8px 0' }}
              />
            </div>
          </div>
        </div>

        {/* Employees Table */}
        {filteredEmployees.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-users fa-4x text-muted mb-4"></i>
            <h4 className="text-muted">
              {selectedLocation?.id ? `No employees at ${selectedLocation.companyName || selectedLocation.locationName}` : 'No employees yet'}
            </h4>
            <p className="text-muted">
              {selectedLocation?.id ? 'Add employees to this location to track who operates each POS session.' : 'Add employees to track who operates each POS session'}
            </p>
          </div>
        ) : (
          <div className="card" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{ fontSize: '0.9rem' }}>
                <thead className="thead-light">
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Location</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Pay Rate</th>
                    <th>Passkey</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map(emp => (
                    <tr key={emp.id}>
                      <td className="font-weight-bold">{emp.name}</td>
                      <td>{emp.email || '—'}</td>
                      <td>{emp.phone || '—'}</td>
                      <td>
                        <span className="small text-muted">
                          <i className="fas fa-store mr-1 text-primary"></i>
                          {getLocationName(emp.locationId)}
                        </span>
                      </td>
                      <td>{getRoleBadge(emp.role)}</td>
                      <td>
                        <span
                          className="badge"
                          style={{
                            backgroundColor: emp.status === 'active' ? '#28a74520' : '#dc354520',
                            color: emp.status === 'active' ? '#28a745' : '#dc3545',
                            fontSize: '0.8rem',
                            borderRadius: 8,
                          }}
                        >
                          {emp.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        {emp.payRate != null ? (
                          <span className="font-weight-bold" style={{ fontSize: '0.9rem' }}>${parseFloat(emp.payRate).toFixed(2)}</span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {emp.pinHash ? (
                          <span className="text-success small">
                            <i className="fas fa-lock mr-1"></i>Set
                          </span>
                        ) : (
                          <span className="text-muted small">
                            <i className="fas fa-unlock mr-1"></i>None
                          </span>
                        )}
                      </td>
                      <td className="text-right">
                        <button className="btn btn-sm btn-outline-primary mr-1" style={{ borderRadius: 8 }} onClick={() => openEdit(emp)}>
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-danger" style={{ borderRadius: 8 }} onClick={() => handleDelete(emp)}>
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ borderRadius: 16 }}>
                <div className="modal-header border-0">
                  <h5 className="modal-title font-weight-bold">{editingEmployee ? 'Edit Employee' : 'Add Employee'}</h5>
                  <button type="button" className="close" onClick={() => setShowModal(false)}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Full Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g. Jane Doe"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      placeholder="jane@example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={formData.phone}
                      onChange={e => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="form-group">
                    <label>Role <span className="text-danger">*</span></label>
                    <select
                      className="form-control"
                      value={formData.role}
                      onChange={e => setFormData({ ...formData, role: e.target.value })}
                    >
                      {ROLES.map(r => (
                        <option key={r.key} value={r.key}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Location <span className="text-danger">*</span></label>
                    <select
                      className="form-control"
                      value={formData.locationId}
                      onChange={e => setFormData({ ...formData, locationId: e.target.value })}
                    >
                      <option value="">Select Location</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.companyName || loc.locationName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Pay Rate</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        value={formData.payRate}
                        onChange={e => setFormData({ ...formData, payRate: e.target.value })}
                        placeholder="00.00"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      className="form-control"
                      value={formData.status}
                      onChange={e => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>
                      Passkey {editingEmployee && editingEmployee.pinHash ? '(leave blank to keep current)' : ''}
                    </label>
                    <input
                      type="password"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      className="form-control"
                      value={formData.pin}
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setFormData({ ...formData, pin: val });
                      }}
                      placeholder="4-6 digit PIN (optional)"
                    />
                    <small className="form-text text-muted">
                      Optional. Employees can use a passkey to clock in/out from the POS screensaver and to verify identity when starting or closing POS sessions.
                    </small>
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button className="btn btn-outline-secondary" style={{ borderRadius: 8 }} onClick={() => setShowModal(false)}>Cancel</button>
                  <button className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: 8 }} onClick={handleSave}>
                    {editingEmployee ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
    </SetupGuard>
  );

  if (loading) {
    const loader = (
      <div className="text-center py-5">
        <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
      </div>
    );
    if (embedded) return loader;
    return <AdminLayout title="Employees">{loader}</AdminLayout>;
  }

  if (embedded) return content;
  return <AdminLayout title="Employees">{content}</AdminLayout>;
};

export default EmployeesPage;
